/**
 * Uploading an original video from the browser: parts of 16 MB straight to the bucket on URLs the
 * backend signed, four at a time, each retried; then the backend completes the upload and queues
 * the encode. A failure cancels the upload, which releases its storage reservation.
 */

import { media } from "@/api/admin";
import type { AdminClient } from "@/api/client";

export function videoDuration(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    const done = (value: number | undefined) => { URL.revokeObjectURL(url); resolve(value); };
    video.preload = "metadata";
    video.onloadedmetadata = () => done(Number.isFinite(video.duration) && video.duration > 0 ? video.duration : undefined);
    video.onerror = () => done(undefined);
    video.src = url;
  });
}

/** `onStarted` gets the new asset's id as soon as the backend has made it, before any byte is sent (an item can name its video at once). */
export async function uploadVideo(client: AdminClient, file: File, title: string, ladder: string, onProgress: (fraction: number) => void, signal: AbortSignal, onStarted?: (assetId: string) => void): Promise<string> {
  const durationSeconds = await videoDuration(file);
  const start = await media.startVideo(client, { title, bytes: file.size, contentType: file.type || "video/mp4", durationSeconds, ladder });
  onStarted?.(start.asset.id);
  const etags: Array<{ partNumber: number; etag: string }> = [];
  let sent = 0, next = 0;
  const putPart = async (part: { partNumber: number; url: string }) => {
    const from = (part.partNumber - 1) * start.partBytes;
    const body = file.slice(from, Math.min(file.size, from + start.partBytes));
    for (let attempt = 1; ; attempt++) {
      try {
        const res = await fetch(part.url, { method: "PUT", body, signal });
        const etag = res.headers.get("ETag");
        if (!res.ok || !etag) throw new Error(`part ${part.partNumber}: ${res.status}`);
        etags.push({ partNumber: part.partNumber, etag });
        sent += body.size;
        onProgress(sent / file.size);
        return;
      } catch (error) {
        if (signal.aborted || attempt >= 3) throw error;
        await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
    }
  };
  try {
    await Promise.all(Array.from({ length: Math.min(4, start.parts.length) }, async () => {
      while (next < start.parts.length) await putPart(start.parts[next++]!);
    }));
    await media.completeVideo(client, start.asset.id, start.uploadId, ladder, etags);
    return start.asset.id;
  } catch (error) {
    await media.abortVideo(client, start.asset.id, start.uploadId).catch(() => undefined);
    throw error;
  }
}
